import $ from "jquery"
import React, { useEffect, useState } from "react"
import { FormattedMessage, FormattedNumber, injectIntl } from "react-intl"
import { useSelector } from "react-redux"
import GlobalHelper from "../../../common/GlobalHelper"
import ActionButton from "../../../common/button/actionbutton.component"
import Icon from "../../../common/icon/icon.component"
import LoadingSpinner from "../../../common/spinner/loading.component"
import Integrations from "../../../integration/Integrations"
import ProcessedPayment from "../../../payment-account/model/ProcessedPayment"
import PaymentProcessingService from "../../../payment-account/service/paymentprocessingService"
import Guard, { Permissions } from "../../../role/Guard"
import CreditMemo from "../../../state/credit-memo/CreditMemo"
import CreditMemoService from "../../../state/credit-memo/CreditMemoService"
import CustomerAccount from "../../../state/customer-account/models/CustomerAccount"
import CustomerAccountService from "../../../state/customer-account/service/CustomerAccountService"
import { isError } from "../../../state/helpers/Error"
import IntlProps from "../../../state/helpers/IntlProps"
import { PAYMENT_SOURCE, PAYMENT_TYPE } from "../../../state/helpers/Payment"
import { INTEGRATION_KEY } from "../../../state/integration/Constants"
import { InvoiceImportableDto } from "../../../state/integration/InvoiceImportable"
import IntegrationImportableService from "../../../state/integration/InvoiceImportableService"
import {
    AdditionalMethodForm,
    NoRefundPayload,
    ORIGINAL_METHOD_PAYMENT_TYPE,
    OnAccountRefundPayload,
    PaymentRefundForm,
    PaymentRefundPayload,
    PaymentRowForm,
    REFUND_TYPE,
    displayablePaymentTypes,
    refundToVoidMap
} from "../../../state/invoice/Constants"
import Invoice, { InvoiceDto } from "../../../state/invoice/models/invoice"
import { InvoiceService } from "../../../state/invoice/services"
import { useGetPaymentTypeOptions } from "../../../state/payment-type/useGetPaymentTypeOptions"
import Payment from "../../../state/payment/Payment"
import PaymentTransaction from "../../../state/payment/PaymentTransaction"
import { PricingTableItemsStore } from "../../../state/pricing/reducers"
import { getPricingTableTotals } from "../../../state/pricing/selectors"
import { BILLING_METHOD } from "../../../state/product/Types"
import { floatSafeAdd, floatSafeSubstract } from "../../../utils/number"
import AdditionalRefundMethods from "../AdditionalRefundMethods/AdditionalRefundMethods"
import PaymentRefundTable from "../PaymentRefundTable/PaymentRefundTable"
import "./PaymentRefundModal.scss"

const uuidv4 = require("uuid/v4")

type OwnProps = {
    id: string,
    invoice?: InvoiceDto,
    creditMemoId?: string,
    void?: boolean,
    onClose: () => void,
    onRefund: (type: REFUND_TYPE, payload: PaymentRefundPayload | OnAccountRefundPayload | NoRefundPayload) => void,
}

const PaymentRefundModal = (props: OwnProps & IntlProps) => {
    const hasLSRIntegration = !!GlobalHelper.GetEnabledIntegrationConfigByKey(INTEGRATION_KEY.LIGHTSPEED_RETAIL).length

    const totalAmount = useSelector((state: PricingTableItemsStore) =>  {
        const totals = getPricingTableTotals(state, props.invoice?.id ?? "")
        const oneTimeTotal = totals?.find(total => total.attributes.billingMethod === BILLING_METHOD.ONE_TIME)
        if (oneTimeTotal) {
            return oneTimeTotal.attributes.totalAmount
        }
    })

    const [loadingPayments, setLoadingPayments] = useState<boolean>(false)
    const [invoice, setInvoice] = useState<Invoice | undefined>()
    const [customerAccount, setCustomerAccount] = useState<CustomerAccount | undefined>()
    const [creditMemo, setCreditMemo] = useState<CreditMemo | undefined>()
    const [externalInvoicesToVoid, setExternalInvoicesToVoid] = useState<InvoiceImportableDto[]>([]) // only for LS-retail
    const [form, setForm] = useState<PaymentRefundForm>({
        processsedTableForm: {
            isAllChecked: false,
            rows: [],
        },
        pendingTableForm: {
            isAllChecked: false,
            rows: [],
        },
        additionalMethodsForm: [],
        notes: "",
    })

    const [refundType, setRefundType] = useState<REFUND_TYPE>(props.void && Number(props.invoice?.attributes.paidAmount) === 0 ?
        REFUND_TYPE.NO_REFUND : REFUND_TYPE.REFUND)
    const [notes, setNotes] = useState<string>("")
    const { loading: loadingPaymentTypes, paymentTypeOptions } = useGetPaymentTypeOptions(props.intl)
    const REFUND_TYPES = [
        ...props.void ? [
            {
                id: REFUND_TYPE.NO_REFUND,
                icon: <Icon value="times" />,
                name: <FormattedMessage
                    id="payment_refund_modal.refund_type.no-refund"
                    defaultMessage="No Refund"
                />,
            }
        ] : [],
        {
            id: REFUND_TYPE.REFUND,
            icon: <Icon value="hand-holding-usd" />,
            name: <FormattedMessage
                id="payment_refund_modal.refund_type.refund"
                defaultMessage="Refund"
            />,
        },
        {
            id: REFUND_TYPE.REFUND_ACCOUNT,
            icon: <Icon value="piggy-bank" />,
            name: <FormattedMessage
                id="payment_refund_modal.refund_type.account"
                defaultMessage="On Account"
            />,
        },
    ]

    useEffect(() => {
        const getPaymentRows = (paymentTransactions: PaymentTransaction[], processedPayments: ProcessedPayment[]): PaymentRowForm[] => {
            return paymentTransactions
                .map(paymentTransaction => mapPaymentTransactionToTableRow(
                    paymentTransaction,
                    processedPayments.find(processed => processed.getPaymentId() === paymentTransaction.getPayment().getId()),
                    processedPayments.find(processed => processed.getPaymentId() === paymentTransaction.getCreditMemoPayment()?.getId()),
                ))
                .filter(paymentTransaction => paymentTransaction.approved && paymentTransaction.refundableAmount)
        }

        const populateForm = async (invoice: Invoice) => {
            setLoadingPayments(true)
            const associatedInvoice = invoice.getReturnOfInvoiceId()
                ? await InvoiceService.get(invoice.getReturnOfInvoiceId())
                : await InvoiceService.get(invoice.getId())

            if (isError(associatedInvoice)) {
                return
            }

            if (hasLSRIntegration) {
                const invoicesToBeVoided = await IntegrationImportableService.getUnVoidedExternalInvoices(invoice.getAllExternalLinks())
                if(isError(invoicesToBeVoided)) {
                    return
                }
                setExternalInvoicesToVoid(invoicesToBeVoided)
            }

            const paymentTransactions = associatedInvoice ?
                (associatedInvoice as Invoice).getPaymentTransactions().
                    filter(paytrans => !paytrans.isDeleted()) : []

            const paymentIds = paymentTransactions.reduce((ids: string[], paymentTransaction: PaymentTransaction) => {
                ids.push(paymentTransaction.getPayment().getId())
                const creditMemoPayment = paymentTransaction.getCreditMemoPayment()
                if (creditMemoPayment) {
                    ids.push(creditMemoPayment.getId())
                }
                return ids
            }, [])
            const processedPayments = await fetchProcessedPayments(paymentIds)
            const paymentRows = getPaymentRows(paymentTransactions, processedPayments)

            setForm({
                processsedTableForm: {
                    isAllChecked: false,
                    rows: paymentRows.filter(payment => !payment.processing && !payment.creditMemoPaymentDetails?.processing)
                },
                pendingTableForm: {
                    isAllChecked: false,
                    rows: paymentRows.filter(payment => payment.processing || payment.creditMemoPaymentDetails?.processing)
                },
                additionalMethodsForm: [],
                notes: "",
            })

            const customerAccount = await CustomerAccountService.getCustomerAccount(invoice.getContactId())
            setCustomerAccount(customerAccount)

            setLoadingPayments(false)
        }

        if (props.invoice) {
            const invoiceObj = new Invoice(props.invoice)
            setInvoice(invoiceObj)
            populateForm(invoiceObj)
        }

    }, [props.invoice])

    useEffect(() => {
        const getPaymentRows = (payments: Payment[], processedPayments: ProcessedPayment[]): PaymentRowForm[] => {
            return payments
                .map(payment => mapPaymentToTableRow(
                    payment,
                    processedPayments.find(processed => processed.getPaymentId() === payment.getId())
                ))
                .filter(payment => payment.approved)
        }

        const populateForm = async (id: string) => {
            setLoadingPayments(true)
            const creditMemo = await CreditMemoService.get(id)

            if (isError(creditMemo)) {
                return
            }

            const creditMemoObj = creditMemo as CreditMemo
            setCreditMemo(creditMemoObj)
            const payment = creditMemoObj.getPayment()

            if (payment) {
                const processedPayments = await fetchProcessedPayments([payment.getId()])
                const paymentRows = getPaymentRows([payment], processedPayments)

                setForm({
                    processsedTableForm: {
                        isAllChecked: false,
                        rows: paymentRows.filter(payment => !payment.processing)
                    },
                    pendingTableForm: {
                        isAllChecked: false,
                        rows: paymentRows.filter(payment => payment.processing)
                    },
                    additionalMethodsForm: [],
                    notes: "",
                })
            }
            if (creditMemoObj) {
                const customerAccount = await CustomerAccountService.getCustomerAccount(creditMemoObj.getContactId())
                setCustomerAccount(customerAccount)
            }

            setLoadingPayments(false)
        }

        if (props.creditMemoId) {
            populateForm(props.creditMemoId)
        }
    }, [props.creditMemoId])

    useEffect(() => {
        $("#" + props.id).modal("show")
    }, [props.id])

    const fetchProcessedPayments = async (ids: string[]): Promise<ProcessedPayment[]> => {
        const processedPayments = await PaymentProcessingService.getProcessedPaymentsByPaymentIds(ids)

        if (isError(processedPayments)) {
            return []
        }

        return processedPayments.map(processedPayment => new ProcessedPayment(processedPayment))
    }

    const getCustomerOwingBalance = (): number => {
        if (customerAccount) {
            const customerGlobalBalance = parseFloat(customerAccount.getGlobalBalance())
            if (customerGlobalBalance === 0) {
                return 0
            }

            // If the owing balance is positive (indicating a credit), it returns 0.
            // If the owing balance is negative (indicating a debt), it returns the absolute value of the owing balance.
            return customerGlobalBalance > 0 ? 0  : Math.abs(customerGlobalBalance)
        }
        return 0
    }

    const getCustomerCreditLimitExceededAmount = (): number => {
        if (customerAccount) {
            const globalBalance = parseFloat(customerAccount.getGlobalBalance())
            const globalBalanceCalc = globalBalance * -1
            const totalRefundExludingCredit = getTotalRefundAmountExcluding(PAYMENT_TYPE.CREDIT)
            const creditLimit = customerAccount.getCreditLimit()
            const newTotalBalance =  globalBalanceCalc + totalRefundExludingCredit - creditLimit

            // If the new total balance is negative (indicating the customer is within their credit limit), it returns 0.
            // Otherwise, it returns the new total balance, representing the amount by which the credit limit is exceeded.
            return newTotalBalance < 0 ? 0 : newTotalBalance
        }
        return 0
    }

    const isGreaterThanCreditLimit = (): boolean => {
        if (customerAccount) {
            const creditLimit = customerAccount.getCreditLimit()
            const globalBalance = parseFloat(customerAccount.getGlobalBalance())
            const totalRefundExludingCredit = getTotalRefundAmountExcluding(PAYMENT_TYPE.CREDIT)
            const globalBalanceCalc = globalBalance * -1
            const newGlobalBalance = globalBalanceCalc + totalRefundExludingCredit

            return creditLimit !== 0 && newGlobalBalance > creditLimit
        }

        return false
    }


    const mapPaymentTransactionToTableRow = (
        paymentTransaction: PaymentTransaction,
        processedPayment?: ProcessedPayment,
        creditMemoProcessedPayment?: ProcessedPayment,
    ): PaymentRowForm => {
        const tableRow = mapPaymentToTableRow(paymentTransaction.getPayment(), processedPayment)
        const creditMemoPayment = paymentTransaction.getCreditMemoPayment()
        return {
            ...tableRow,
            paymentTransactionId: paymentTransaction.getId(),
            refundableAmount: Number(paymentTransaction.getRefundableAmount()),
            refundable: !tableRow.processing || !paymentTransaction.isAssignedToMultipleDocuments(),
            creditMemoPaymentDetails: creditMemoPayment && mapPaymentToTableRow(creditMemoPayment, creditMemoProcessedPayment)
        }
    }

    const mapPaymentToTableRow = (payment: Payment, processedPayment?: ProcessedPayment): PaymentRowForm => {
        const approved = processedPayment ? processedPayment.isPaymentApproved() : payment.isApproved()
        const processing = processedPayment ? processedPayment.isPaymentProcessing() : payment.isProcessing()
        return {
            id: uuidv4(),
            paymentTransactionId: "",
            paymentId: payment.getId(),
            identifier: payment.getIdentifier(),
            refundableAmount: Number(payment.getRefundableAmount()),
            currency: payment.getCurrency(),
            creditCardDetails: payment.getIntegratedPaymentCreditCardDetails(),
            bankAccountDetails: payment.getIntegratedPaymentBankDetails(),
            paymentType: payment.getType(),
            selected: false,
            refundAmount: 0,
            refundMethod: "",
            approved,
            refundable: true,
            processing,
            integrationKey: payment.getIntegratedPaymentIntegrationKey(),
        }
    }

    const handleClose = event => {
        event.preventDefault()
        $("#" + props.id).modal("hide")
        if (props.onClose) {
            props.onClose()
        }
    }

    const handleRefund = event => {
        event.preventDefault()

        if (refundType === REFUND_TYPE.REFUND) {
            props.onRefund(REFUND_TYPE.REFUND, getPaymentRefundPayload())
        } else if (refundType === REFUND_TYPE.REFUND_ACCOUNT) {
            props.onRefund(REFUND_TYPE.REFUND_ACCOUNT, getOnAccountRefundPayload())
        }
    }

    const getTotalAmountToRefund = (): string => {
        if (creditMemo) {
            return `${-((parseFloat(creditMemo.getRemainingAmount())) - getLeftToRefund())}`
        }


        if (props.void && invoice) {
            return `${-invoice.getPaidAmount()}`
        }

        if (invoice && totalAmount) {
            if (totalAmount != invoice.getPaidAmount()) {
                return invoice.getBalance()
            }
            return totalAmount
        }

        return "0"
    }

    const getTotalRefundAmountExcluding = (refundMethod: string): number => {
        return getPaymentRefundPayload().selectedPayments
            .filter(row => row.amountToRefund && row.refundMethod !== refundMethod )
            .map(row => Math.abs(parseFloat(row.amountToRefund)))
            .reduce((total, amount) => total + amount, 0)
    }

    const handleVoid = event => {
        event.preventDefault()

        if (refundType === REFUND_TYPE.REFUND || refundType === REFUND_TYPE.REFUND_ACCOUNT) {
            props.onRefund(refundType, getPaymentRefundPayload())
        } else if (refundType === REFUND_TYPE.NO_REFUND) {
            props.onRefund(REFUND_TYPE.NO_REFUND, getNoRefundPayload())
        }
    }

    const getPaymentRefundPayload = (): PaymentRefundPayload => ({
        returnId: invoice?.getId() ?? "",
        creditMemoId: creditMemo?.getId() ?? "",
        amount: getTotalAmountToRefund(),
        invoiceId: invoice?.getId() ?? invoice?.getReturnOfInvoiceId() ?? "",
        notes,
        currency: getCurrency(),
        contactId: invoice?.getContactId() ?? creditMemo?.getContactId() ?? "",
        source: PAYMENT_SOURCE.BACKOFFICE,
        fingerprintId: GlobalHelper.getFingerprintId(),
        voidAction: refundToVoidMap[refundType],
        selectedPayments: [
            ...form.processsedTableForm.rows,
            ...form.pendingTableForm.rows,
            ...form.additionalMethodsForm,
        ]
            .filter(row => row.refundAmount)
            .map(row => {
                const creditMemoPaymentDetails = (row as PaymentRowForm).creditMemoPaymentDetails
                return {
                    paymentId: row.hasOwnProperty("paymentId") ? (row as PaymentRowForm).paymentId : "",
                    paymentTransactionId: row.hasOwnProperty("paymentTransactionId") ? (row as PaymentRowForm).paymentTransactionId : "",
                    amountToRefund: `${-row.refundAmount}`,
                    refundMethod: getRefundPayloadRefundMethod(row, row.refundMethod),
                    customPaymentTypeId: getRefundPayloadCustomPaymentTypeId(row),
                    isProcessedRefund: isProcessedRefund(row, row.refundMethod),
                    originalPayment: creditMemoPaymentDetails && {
                        paymentId: creditMemoPaymentDetails.hasOwnProperty("paymentId") ? creditMemoPaymentDetails.paymentId : "",
                        refundMethod: getRefundPayloadRefundMethod(creditMemoPaymentDetails, row.refundMethod),
                        isProcessedRefund: isProcessedRefund(creditMemoPaymentDetails, row.refundMethod),
                    }
                }
            })
    })

    const getExternalInvoicesToVoid =  (): InvoiceImportableDto[] => {
        if (props.invoice && invoice && hasLSRIntegration) {
            const externalLinks = invoice.getAllExternalLinks()

            const isInvoiceExternalId = (externalLink, invoiceImportableDto) =>
                externalLink.attributes.externalId === invoiceImportableDto.saleId &&
                externalLink.attributes.integrationKey === invoiceImportableDto.integrationKey

            return externalInvoicesToVoid.filter(invoiceImportableDto =>
                externalLinks.some(link => isInvoiceExternalId(link, invoiceImportableDto)))
        }
        return []
    }


    const getOnAccountRefundPayload = (): OnAccountRefundPayload => {

        let amount = "0"
        if (props.void) {
            amount = invoice?.getTotalAmount() ?? "0"
        }

        if (invoice && totalAmount) {
            amount = totalAmount
            if(totalAmount !== invoice.getBalance()) {
                amount = invoice.getBalance()
            }
        }

        return {
            note: notes,
            notes,
            amount: amount,
            paymentType: PAYMENT_TYPE.CREDIT,
            currency: getCurrency(),
            contactId: invoice?.getContactId() ?? "",
            source: PAYMENT_SOURCE.BACKOFFICE,
            fingerprintId: GlobalHelper.getFingerprintId(),
            transactions: [{
                invoiceId: invoice?.getId() ?? "",
                amount: amount,
            }],
            voidAction: refundToVoidMap[refundType],
            locationId : invoice?.getLocationId() ?? "",
        }
    }

    const getNoRefundPayload = (): NoRefundPayload => ({
        invoiceId: invoice?.getId(),
        notes: notes,
        currency: invoice?.getCurrency() ?? "",
        contactId: invoice?.getContactId() ?? "",
        source: PAYMENT_SOURCE.BACKOFFICE,
        fingerprintId: GlobalHelper.getFingerprintId(),
        voidAction: refundToVoidMap[refundType]
    })

    const isProcessedRefund = (row: PaymentRowForm | AdditionalMethodForm, refundMethod: string): boolean => {
        return refundMethod === ORIGINAL_METHOD_PAYMENT_TYPE
            && row.hasOwnProperty("integrationKey")
            && (row as PaymentRowForm).integrationKey === INTEGRATION_KEY.RAINFOREST
    }

    const getRefundPayloadRefundMethod = (row: PaymentRowForm | AdditionalMethodForm, refundMethod: string): string => {
        if (refundMethod === ORIGINAL_METHOD_PAYMENT_TYPE) {
            const paymentRowForm = row as PaymentRowForm
            return (paymentRowForm.creditMemoPaymentDetails || paymentRowForm).paymentType
        }

        if (
            displayablePaymentTypes.includes(refundMethod)
        ) {
            return refundMethod
        }

        return paymentTypeOptions.find(option => option.value === refundMethod)?.label as string ?? refundMethod
    }

    const getRefundPayloadCustomPaymentTypeId = (row: PaymentRowForm | AdditionalMethodForm): string => {
        if (row.refundMethod === ORIGINAL_METHOD_PAYMENT_TYPE) {
            if (
                displayablePaymentTypes.includes((row as PaymentRowForm).paymentType)
            ) {
                return ""
            } else {
                return paymentTypeOptions.find(option => (row as PaymentRowForm).paymentType === option.label)?.value ?? ""
            }
        }

        if (
            displayablePaymentTypes.includes(row.refundMethod)
        ) {
            return ""
        }


        return row.refundMethod
    }

    const getDefaultPaymentMethod = (row: PaymentRowForm) => {
        if (refundType === REFUND_TYPE.REFUND_ACCOUNT) {
            return PAYMENT_TYPE.CREDIT
        }

        return !row.integrationKey || row.integrationKey === INTEGRATION_KEY.RAINFOREST
            ? ORIGINAL_METHOD_PAYMENT_TYPE
            : PAYMENT_TYPE.CREDIT_CARD
    }

    const handleSelectAllRows = (formKey: string, selected: boolean) => {
        const tableForm = form[formKey]
        const amountsToRefund: number[] = []

        if (selected) {
            let leftToRefund = getLeftToRefund()
            tableForm.rows.forEach(row => {
                if (row.selected) {
                    amountsToRefund.push(row.refundAmount)
                } else {
                    const amount = Math.min(leftToRefund, row.refundableAmount)
                    amountsToRefund.push(amount)
                    leftToRefund -= amount
                }
            })
        }

        setForm({
            ...form,
            [formKey]: {
                ...tableForm,
                isAllChecked: selected,
                rows: tableForm.rows.map((row, i) => ({
                    ...row,
                    refundMethod: row.refundable && selected ? getDefaultPaymentMethod(row) : "",
                    refundAmount: row.refundable && selected ? amountsToRefund[i] : 0,
                    selected: row.refundable && selected,
                }))
            }
        })
    }

    const handleSelectRow = (formKey: string, id: string, selected: boolean) => {
        const tableForm = form[formKey]
        const index = tableForm.rows.findIndex(row => row.id === id)

        if (index !== -1) {
            const row: PaymentRowForm = tableForm.rows[index]
            let amountToSet = 0

            if (row.refundable && selected) {
                amountToSet = row.processing ? row.refundableAmount : Math.min(getLeftToRefund(), row.refundableAmount)
            }

            const rows = [
                ...tableForm.rows.slice(0, index),
                {
                    ...row,
                    refundMethod: row.refundable && selected ? getDefaultPaymentMethod(row) : "",
                    refundAmount: amountToSet,
                    selected: row.refundable && selected,
                },
                ...tableForm.rows.slice(index + 1)
            ]
            setForm({
                ...form,
                [formKey]: {
                    ...tableForm,
                    isAllChecked: rows.filter(row => row.refundable).every(row => row.selected),
                    rows,
                }
            })
        }
    }

    const handleEditRowRefundAmount = (formKey: string, id: string, amount: number) => {
        const tableForm = form[formKey]
        const index = tableForm.rows.findIndex(row => row.id === id)

        if (index !== -1) {
            const row = tableForm.rows[index]

            setForm({
                ...form,
                [formKey]: {
                    ...tableForm,
                    rows: [
                        ...tableForm.rows.slice(0, index),
                        {
                            ...row,
                            refundAmount: Math.min(
                                getLeftToRefund(row.refundAmount),
                                row.refundableAmount,
                                amount
                            )
                        },
                        ...tableForm.rows.slice(index + 1),
                    ]
                }
            })
        }
    }

    const handleEditRowRefundMethod = (formKey: string, id: string, method: string) => {
        const tableForm = form[formKey]
        const index = tableForm.rows.findIndex(row => row.id === id)

        if (index !== -1) {
            setForm({
                ...form,
                [formKey]: {
                    ...tableForm,
                    rows: [
                        ...tableForm.rows.slice(0, index),
                        {
                            ...tableForm.rows[index],
                            refundMethod: method
                        },
                        ...tableForm.rows.slice(index + 1),
                    ]
                }
            })
        }
    }

    const handleAddAdditionalRefundMethod = () => {
        setForm({
            ...form,
            additionalMethodsForm: [
                ...form.additionalMethodsForm,
                {
                    id: uuidv4(),
                    refundAmount: 0,
                    currency: getCurrency(),
                    refundMethod: "",
                }
            ]
        })
    }

    const handleRemoveAdditionalRefundMethod = (id: string) => {
        const index = form.additionalMethodsForm.findIndex(row => row.id === id)

        if (index !== -1) {
            setForm({
                ...form,
                additionalMethodsForm: [
                    ...form.additionalMethodsForm.slice(0, index),
                    ...form.additionalMethodsForm.slice(index + 1),
                ]
            })
        }
    }

    const handleEditAdditionalRefundMethodAmount = (id: string, amount: number) => {
        const index = form.additionalMethodsForm.findIndex(row => row.id === id)

        if (index !== -1) {
            const row = form.additionalMethodsForm[index]

            setForm({
                ...form,
                additionalMethodsForm: [
                    ...form.additionalMethodsForm.slice(0, index),
                    {
                        ...row,
                        refundAmount: Math.min(
                            getLeftToRefund(row.refundAmount),
                            amount
                        )
                    },
                    ...form.additionalMethodsForm.slice(index + 1),
                ]
            })
        }
    }

    const handleEditAdditionalRefundMethodMethod = (id: string, method: string) => {
        const index = form.additionalMethodsForm.findIndex(row => row.id === id)

        if (index !== -1) {
            const row = form.additionalMethodsForm[index]
            const refundAmountToSet = row.refundMethod === "" ? getLeftToRefund() : row.refundAmount

            setForm({
                ...form,
                additionalMethodsForm: [
                    ...form.additionalMethodsForm.slice(0, index),
                    {
                        ...row,
                        refundMethod: method,
                        refundAmount: refundAmountToSet
                    },
                    ...form.additionalMethodsForm.slice(index + 1),
                ]
            })
        }
    }

    const getTotalToRefund = (): number => {
        if (props.void && invoice) {
            return Number(invoice?.getPaidAmount())
        }

        if (invoice && totalAmount) {
            if (totalAmount != invoice.getPaidAmount()) {
                return Number(invoice.getBalance()) * -1
            }
            return Number(totalAmount) * -1
        }

        if (creditMemo) {
            return Number(creditMemo.getRemainingAmount())
        }

        return 0
    }

    const getLeftToRefund = (excludeAmount = 0): number => {
        const totalFromForm = [
            ...form.processsedTableForm.rows,
            ...form.pendingTableForm.rows,
            ...form.additionalMethodsForm
        ].reduce((total: number, row: PaymentRowForm | AdditionalMethodForm) => floatSafeAdd(total, row.refundAmount), 0)
        return floatSafeSubstract(getTotalToRefund(), floatSafeSubstract(totalFromForm, excludeAmount))
    }

    const canRefund = (): boolean => {
        if (props.void && invoice) {
            switch (refundType) {
            case REFUND_TYPE.NO_REFUND:
            case REFUND_TYPE.REFUND_ACCOUNT:
                if (hasLSRIntegration && getExternalInvoicesToVoid().length > 0) {
                    return false
                }
                return true
            case REFUND_TYPE.REFUND:
                return getLeftToRefund() === 0
            default:
                return true
            }
        }

        if (props.creditMemoId && creditMemo) {
            const amountRefund = parseFloat(creditMemo.getRemainingAmount()) -  getLeftToRefund()
            return getLeftToRefund() >= 0 && amountRefund > 0
        }

        return refundType === REFUND_TYPE.REFUND_ACCOUNT || getLeftToRefund() === 0
    }

    const canRefundLSR = (): boolean => {
        if (!hasLSRIntegration) {
            return true // If LSR integration is not enabled, allow refund.
        }

        if (refundType === REFUND_TYPE.REFUND_ACCOUNT) {
            return true
        }

        if (getCustomerOwingBalance() !== 0) {
            return false // Cannot refund if the customer has an owing balance with LSR integration.
        }

        if (isGreaterThanCreditLimit()) {
            return Guard.isAuthorized(Permissions.BypassUpdateCreditLimit)
        }

        return true // Default case: refund is allowed.
    }

    const getIdentifier = (): string => {
        return invoice ? invoice.getIdentifier() : ""
    }

    const isLoading = (): boolean => {
        return loadingPaymentTypes || loadingPayments
    }

    const handleChangeRefundType = (type: REFUND_TYPE) => {
        setRefundType(type)

        if (type === REFUND_TYPE.REFUND_ACCOUNT) {
            setForm({
                ...form,
                processsedTableForm: {
                    ...form.processsedTableForm,
                    rows: form.processsedTableForm.rows.map(row => ({
                        ...row,
                        refundMethod: row.selected ? PAYMENT_TYPE.CREDIT : row.refundMethod,
                    }))
                },
                pendingTableForm: {
                    ...form.pendingTableForm,
                    rows: form.pendingTableForm.rows.map(row => ({
                        ...row,
                        refundMethod: row.selected ? PAYMENT_TYPE.CREDIT : row.refundMethod,
                    }))
                },
                additionalMethodsForm: form.additionalMethodsForm.map(row => ({
                    ...row,
                    refundMethod: PAYMENT_TYPE.CREDIT,
                })),
            })
        }
    }

    const getCurrency = (): string => {
        if (invoice) {
            return invoice.getCurrency()
        }

        if (creditMemo) {
            return creditMemo.getCurrency()
        }

        return ""
    }

    const getRefundTypes = () => {
        if (props.void && invoice) {
            const allDeleted = invoice.getPaymentTransactions().every(payTrans => payTrans.isDeleted())
            if (allDeleted || invoice?.getPaymentTransactions().length === 0) {
                return REFUND_TYPES.filter(type => type.id === REFUND_TYPE.NO_REFUND)
            }
        }


        return props.creditMemoId
            ? REFUND_TYPES.filter(type => type.id !== REFUND_TYPE.REFUND_ACCOUNT)
            : REFUND_TYPES
    }

    const getPaymentTypeOptions = () => {
        if (props.creditMemoId) {
            return paymentTypeOptions.filter(type => type.value !== PAYMENT_TYPE.CREDIT)
        }
        return paymentTypeOptions
    }

    return (
        <>
            <div
                className="modal fade payment-refund-modal"
                id={props.id}
                role="dialog"
                aria-labelledby="refundModalTitle"
                data-backdrop="static"
                data-keyboard="false"
                tabIndex={-1}
            >
                <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title mb-0 border-0 pb-0" id="refundModalTitle">
                                {props.void ? (
                                    <>
                                        <Icon value="ban" className="mr-2" />
                                        <FormattedMessage
                                            id="payment_refund_modal.title_void"
                                            defaultMessage="Void {identifier}"
                                            values={{
                                                identifier: getIdentifier(),
                                            }}
                                        />
                                    </>
                                ) : (
                                    <>
                                        <Icon value="undo" className="mr-2" />
                                        <FormattedMessage
                                            id="payment_refund_modal.title_refund"
                                            defaultMessage="Return {identifier}"
                                            values={{
                                                identifier: getIdentifier(),
                                            }}
                                        />
                                    </>
                                )}
                            </h5>
                            <button type="button" className="close" onClick={handleClose} aria-label="Close">
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>

                        <div className="modal-body">
                            {isLoading() && <LoadingSpinner />}
                            {!isLoading() && (
                                <div className="container-fluid">
                                    <div className="form-group row mb-3">
                                        <div className="col-12 col-md-10 p-0">
                                            <div className="d-flex align-content-stretch flex-wrap">
                                                {getRefundTypes().map(type => (
                                                    <div
                                                        key={type.id}
                                                        className="col-xl-3 col-lg-4 align-self-stretch py-1 pl-0 pr-2 no-underline"
                                                    >
                                                        <a onClick={() => handleChangeRefundType(type.id)}>
                                                            <div className={
                                                                `card h-100 button-box-select
                                                                ${refundType === type.id ? " bg-primary text-white" : ""}`
                                                            }>
                                                                <div className="card-body px-1">
                                                                    <h5 className="card-title m-0 p-0 border-0">
                                                                        <p className="text-center m-0">
                                                                            {type.icon}
                                                                        </p>
                                                                    </h5>
                                                                    <h6 className="card-title m-0 p-0 border-0">
                                                                        <p className="text-center m-0 pt-1">
                                                                            {type.name}
                                                                        </p>
                                                                    </h6>
                                                                </div>
                                                            </div>
                                                        </a>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    {refundType === REFUND_TYPE.REFUND && form.processsedTableForm.rows.length > 0 && (
                                        <div className="form-group row mb-3">
                                            <PaymentRefundTable
                                                title={
                                                    <FormattedMessage
                                                        id="payment_refund_modal.processed_payment_refund_table.header"
                                                        defaultMessage="Processed payments related to this refund ({total})"
                                                        values={{
                                                            total: form.processsedTableForm.rows.length
                                                        }}
                                                    />
                                                }
                                                refundType={refundType}
                                                leftToRefund={getLeftToRefund()}
                                                form={form.processsedTableForm}
                                                refundMethods={getPaymentTypeOptions()}
                                                editableAmount={!props.void}
                                                onSelectAll={(selected: boolean) => handleSelectAllRows("processsedTableForm", selected)}
                                                onSelectRow={(id: string, selected: boolean) => handleSelectRow("processsedTableForm", id, selected)}
                                                onEditRowRefundAmount={
                                                    (id: string, amount: number) => handleEditRowRefundAmount("processsedTableForm", id, amount)
                                                }
                                                onEditRowRefundMethod={
                                                    (id: string, method: string) => handleEditRowRefundMethod("processsedTableForm", id, method)
                                                }
                                            />
                                        </div>
                                    )}
                                    {refundType === REFUND_TYPE.REFUND && form.pendingTableForm.rows.length > 0 && (
                                        <div className="form-group row mb-3">
                                            <PaymentRefundTable
                                                title={
                                                    <FormattedMessage
                                                        id="payment_refund_modal.pending_payment_refund_table.header"
                                                        defaultMessage="Pending payments related to this refund ({total})"
                                                        values={{
                                                            total: form.pendingTableForm.rows.length
                                                        }}
                                                    />
                                                }
                                                subtitle={
                                                    <FormattedMessage
                                                        id="payment_refund_modal.pending_payment_refund_table.sub_header"
                                                        defaultMessage="Pending payments will be voided instead of refunded,
                                                            and can only be voided if they have been applied to a single document."
                                                    />
                                                }
                                                refundType={refundType}
                                                leftToRefund={getLeftToRefund()}
                                                form={form.pendingTableForm}
                                                refundMethods={getPaymentTypeOptions()}
                                                editableAmount={!props.void}
                                                onSelectRow={
                                                    (id: string, selected: boolean) => handleSelectRow("pendingTableForm", id, selected)
                                                }
                                                onEditRowRefundMethod={
                                                    (id: string, method: string) => handleEditRowRefundMethod("pendingTableForm", id, method)
                                                }
                                                collapsable
                                                collapsed={form.processsedTableForm.rows.length > 0}
                                            />
                                        </div>
                                    )}
                                    {!props.void && refundType === REFUND_TYPE.REFUND && (
                                        <div className="form-group row mb-3">
                                            <AdditionalRefundMethods
                                                refundType={refundType}
                                                leftToRefund={getLeftToRefund()}
                                                form={form.additionalMethodsForm}
                                                refundMethods={
                                                    getPaymentTypeOptions().filter(method => method.value !== ORIGINAL_METHOD_PAYMENT_TYPE)
                                                }
                                                onAdd={handleAddAdditionalRefundMethod}
                                                onRemove={handleRemoveAdditionalRefundMethod}
                                                onEditRefundAmount={handleEditAdditionalRefundMethodAmount}
                                                onEditRefundMethod={handleEditAdditionalRefundMethodMethod}
                                            />
                                        </div>
                                    )}
                                    {[REFUND_TYPE.REFUND_ACCOUNT, REFUND_TYPE.NO_REFUND].includes(refundType) &&
                                     getExternalInvoicesToVoid().length > 0 &&
                                    <div className="form-group row mb-1">
                                        <div className="col-10 mb-3">
                                            <FormattedMessage
                                                id="invoice.analytics.delete_message.integration_warning_message"
                                                defaultMessage="If you wish to continue, you must first void these sales: "
                                            />
                                            {getExternalInvoicesToVoid().map((link, i) => {
                                                const url = Integrations.getExternalSaleUrl(link)
                                                return <div key={i}>
                                                    <a href={url} target="_blank" rel="noreferrer">
                                                        #{link.saleId}
                                                    </a>
                                                </div>
                                            })
                                            }
                                        </div>
                                    </div>}
                                    {refundType === REFUND_TYPE.REFUND_ACCOUNT && (
                                        <p>
                                            <FormattedMessage
                                                id="payment_refund_modal.on_account_refund.description"
                                                defaultMessage="One or more credit memos will be created
                                                for a total amount of {total} on the account."
                                                values={{
                                                    total: (
                                                        <b>
                                                            <FormattedNumber
                                                                style="currency"
                                                                currency={getCurrency()}
                                                                value={getTotalToRefund()}
                                                            />
                                                        </b>
                                                    )
                                                }}
                                            />
                                        </p>
                                    )}
                                    <div className="form-group row mb-3">
                                        <h5>
                                            <FormattedMessage
                                                id="payment_refund_modal.notes.title."
                                                defaultMessage="Notes"
                                            />
                                        </h5>
                                        <textarea
                                            name="notes"
                                            value={notes}
                                            onChange={({ target }) => setNotes(target.value)}
                                            id="notes"
                                            rows={5}
                                            className="form-control"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer">
                            {refundType === REFUND_TYPE.REFUND && (invoice || creditMemo) && (
                                <div className="form-group row left-to-refund">
                                    <table className="table">
                                        <tbody>
                                            <tr>
                                                <td className="label-col">
                                                    <b>
                                                        <FormattedMessage
                                                            id="payment_refund_modal.total"
                                                            defaultMessage="Total"
                                                        />
                                                    </b>
                                                    <span>
                                                        <FormattedMessage
                                                            id="payment_refund_modal.left_to_refund"
                                                            defaultMessage="left to refund"
                                                        />
                                                    </span>
                                                </td>
                                                <td className="total-col">
                                                    <b>
                                                        <FormattedNumber
                                                            style="currency"
                                                            currency={getCurrency()}
                                                            value={getLeftToRefund()}
                                                        />
                                                    </b>
                                                </td>
                                                <td className="spacer-col" />
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            { refundType === REFUND_TYPE.REFUND && getCustomerOwingBalance() > 0 && canRefund() ?
                                <div className="actions text-danger">
                                    <Icon value="exclamation" />
                                    <em>
                                        <FormattedMessage
                                            id="invoice.refund_method.customer_unpaid_balance_info"
                                            defaultMessage="This customer has an owing balance of {amount}."
                                            values={{
                                                amount: <strong>
                                                    <FormattedNumber
                                                        value={getCustomerOwingBalance()}
                                                        style={"currency"}
                                                        currency={invoice?.getCurrency() || creditMemo?.getCurrency()}
                                                    />
                                                </strong>,
                                            }}
                                        />
                                    </em>
                                </div>
                                :
                                refundType === REFUND_TYPE.REFUND && isGreaterThanCreditLimit() && canRefund() &&
                                    <div className="actions text-danger">
                                        <Icon value="exclamation" />
                                        <em>
                                            <FormattedMessage
                                                id="invoice.refund_method.customer_credit_limit_is_exceeded"
                                                defaultMessage="Issuing this refund will exceed the customer's credit limit by {amount}"
                                                values={{
                                                    amount:
                                                    <strong>
                                                        <FormattedNumber
                                                            value={getCustomerCreditLimitExceededAmount()}
                                                            style={"currency"}
                                                            currency={invoice?.getCurrency() || creditMemo?.getCurrency()}
                                                        />
                                                    </strong>,
                                                }}
                                            />
                                        </em>
                                    </div>
                            }
                            <div className="actions">
                                <button type="button" className="btn btn-secondary" onClick={handleClose}>
                                    <FormattedMessage
                                        id="payment_refund_modal.close_btn"
                                        defaultMessage="Close"
                                    />
                                </button>
                                {props.void ? (
                                    <ActionButton
                                        type="button"
                                        className="btn btn-danger"
                                        onClick={handleVoid}
                                        disabled={!canRefund()}
                                        isLoading={false}
                                    >
                                        <FormattedMessage
                                            id="payment_refund_modal.void_btn"
                                            defaultMessage="Void"
                                        />
                                    </ActionButton>
                                ) : (
                                    <ActionButton
                                        type="button"
                                        className="btn btn-warning"
                                        onClick={handleRefund}
                                        disabled={!canRefund() || !canRefundLSR()}
                                        isLoading={false}
                                    >
                                        <FormattedMessage
                                            id="payment_refund_modal.refund_btn"
                                            defaultMessage="Refund"
                                        />
                                    </ActionButton>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default injectIntl(PaymentRefundModal)



// WEBPACK FOOTER //
// ./src/invoice/components/PaymentRefundModal/PaymentRefundModal.tsx